"use client";

import { useCallback, useEffect, useRef } from "react";
import { useTouchControlsSettings } from "../hooks/useTouchControlsSettings";
import type { LegacyAction } from "./TouchControls";
import type { ActiveCell } from "./BattleBoard";

type BoardTouchControlsProps = {
  active: ActiveCell[];
  activeBlockIndex?: number;
  disabled?: boolean;
  onInput: (action: LegacyAction, held: boolean) => void;
};

type HorizontalDirection = "left" | "right" | null;

type GestureState = {
  pointerId: number | null;
  startX: number;
  startPieceCenter: number;
  currentX: number;
  targetY: number;
  startY: number;
  boardRect: DOMRect | null;
  startedAt: number;
  lastMoveAt: number;
  lastMoveY: number;
  slowSwipe: boolean;
  verticalIntent: boolean;
  horizontalDragged: boolean;
};

const BOARD_COLUMNS = 10;
const BOARD_ROWS = 24;
const HORIZONTAL_DEAD_ZONE_CELLS = 0.36;
const DOWNWARD_GESTURE_THRESHOLD_CELLS = 0.75;
const SLOW_SWIPE_MAX_PX_PER_MS = 0.32;
const SLOW_DROP_INTERVAL_MS = 84;

function activeCenter(active: readonly ActiveCell[]): number {
  if (active.length === 0) return 5.5;
  return active.reduce((sum, cell) => sum + cell.x, 0) / active.length;
}

/**
 * Turns the local board into a direct touch surface. Horizontal movement is
 * relative to where the finger started, so a tap alone does not move the
 * piece. A slow downward drag pulses soft drop at half speed; a fast
 * downward flick starts the hard drop when the finger is released. A tap
 * rotates immediately: the left half rotates counter-clockwise and the right
 * half rotates clockwise.
 */
export function BoardTouchControls({ active, activeBlockIndex, disabled = false, onInput }: BoardTouchControlsProps) {
  const { settings } = useTouchControlsSettings();
  const activeRef = useRef(active);
  const gestureRef = useRef<GestureState>({
    pointerId: null,
    startX: 0,
    startPieceCenter: 5.5,
    currentX: 0,
    targetY: 0,
    startY: 0,
    boardRect: null,
    startedAt: 0,
    lastMoveAt: 0,
    lastMoveY: 0,
    slowSwipe: false,
    verticalIntent: false,
    horizontalDragged: false,
  });
  const horizontalRef = useRef<HorizontalDirection>(null);
  const softDropRef = useRef(false);
  const slowDropActiveRef = useRef(false);
  const slowDropTimerRef = useRef<number | undefined>(undefined);
  const hardDropRef = useRef(false);
  const hardDropTargetBlockIndexRef = useRef<number | undefined>(undefined);
  const hardDropFallbackTimerRef = useRef<number | undefined>(undefined);
  const activeBlockIndexRef = useRef(activeBlockIndex);
  const rotationTapCandidateRef = useRef(false);
  const rotationTapXRef = useRef(0);

  const setHorizontal = useCallback((next: HorizontalDirection) => {
    if (horizontalRef.current === next) return;
    if (horizontalRef.current) onInput(horizontalRef.current, false);
    horizontalRef.current = next;
    if (next) onInput(next, true);
  }, [onInput]);

  const setSoftDrop = useCallback((next: boolean) => {
    if (softDropRef.current === next) return;
    softDropRef.current = next;
    onInput("softDrop", next);
  }, [onInput]);

  const setHardDrop = useCallback((next: boolean) => {
    if (hardDropRef.current === next) return;
    hardDropRef.current = next;
    if (!next) {
      hardDropTargetBlockIndexRef.current = undefined;
      if (hardDropFallbackTimerRef.current !== undefined) {
        window.clearTimeout(hardDropFallbackTimerRef.current);
        hardDropFallbackTimerRef.current = undefined;
      }
    }
    onInput("hardDrop", next);
  }, [onInput]);

  const stopSlowDrop = useCallback(() => {
    slowDropActiveRef.current = false;
    if (slowDropTimerRef.current !== undefined) {
      window.clearTimeout(slowDropTimerRef.current);
      slowDropTimerRef.current = undefined;
    }
    setSoftDrop(false);
  }, [setSoftDrop]);

  const startSlowDrop = useCallback(() => {
    if (slowDropActiveRef.current) return;
    slowDropActiveRef.current = true;
    let dropOn = true;
    setSoftDrop(dropOn);

    const pulse = () => {
      if (!slowDropActiveRef.current) return;
      dropOn = !dropOn;
      setSoftDrop(dropOn);
      slowDropTimerRef.current = window.setTimeout(pulse, SLOW_DROP_INTERVAL_MS);
    };
    slowDropTimerRef.current = window.setTimeout(pulse, SLOW_DROP_INTERVAL_MS);
  }, [setSoftDrop]);

  const pulseRotation = useCallback((action: "cw" | "ccw") => {
    onInput(action, true);
    window.setTimeout(() => onInput(action, false), 95);
  }, [onInput]);

  const updateGesture = useCallback(() => {
    const gesture = gestureRef.current;
    const rect = gesture.boardRect;
    if (gesture.pointerId === null || !rect || rect.width <= 0 || rect.height <= 0) return;

    const cellWidth = rect.width / BOARD_COLUMNS;
    const cellHeight = rect.height / BOARD_ROWS;
    const verticalDelta = gesture.targetY - gesture.startY;
    const downward = verticalDelta > cellHeight * DOWNWARD_GESTURE_THRESHOLD_CELLS;
    if (gesture.verticalIntent || downward) {
      // Once the gesture is clearly vertical, never let its diagonal travel
      // leak into a horizontal move on the same input sample.
      setHorizontal(null);
    } else {
      const targetCenter = gesture.startPieceCenter + (gesture.currentX - gesture.startX) / cellWidth;
      const delta = targetCenter - activeCenter(activeRef.current);
      const nextDirection: HorizontalDirection = delta < -HORIZONTAL_DEAD_ZONE_CELLS
        ? "left"
        : delta > HORIZONTAL_DEAD_ZONE_CELLS
          ? "right"
          : null;
      setHorizontal(nextDirection);
    }

    if (downward && gesture.slowSwipe) startSlowDrop();
    else stopSlowDrop();
  }, [setHorizontal, startSlowDrop, stopSlowDrop]);

  const endGesture = useCallback((startHardDrop = false, pointerId?: number) => {
    if (pointerId !== undefined && gestureRef.current.pointerId !== pointerId) return;
    const hadGesture = gestureRef.current.pointerId !== null;
    if (!hadGesture) {
      stopSlowDrop();
      setHardDrop(false);
      return;
    }
    const gesture = gestureRef.current;
    const rect = gesture.boardRect;
    gestureRef.current.pointerId = null;
    gestureRef.current.boardRect = null;
    setHorizontal(null);
    stopSlowDrop();

    const cellHeight = rect && rect.height > 0 ? rect.height / BOARD_ROWS : 0;
    const downward = cellHeight > 0
      && gesture.targetY - gesture.startY > cellHeight * DOWNWARD_GESTURE_THRESHOLD_CELLS;
    const cellWidth = rect && rect.width > 0 ? rect.width / BOARD_COLUMNS : 0;
    const horizontalDragged = gesture.horizontalDragged
      || (cellWidth > 0 && Math.abs(gesture.currentX - gesture.startX) > cellWidth * HORIZONTAL_DEAD_ZONE_CELLS);
    const shouldRotate = rotationTapCandidateRef.current
      && !horizontalDragged
      && !gesture.verticalIntent
      && !downward
      && rect !== null;
    rotationTapCandidateRef.current = false;

    if (startHardDrop && downward && !gesture.slowSwipe && settings.hardDropEnabled) {
      hardDropTargetBlockIndexRef.current = activeBlockIndexRef.current;
      setHardDrop(true);
      if (activeBlockIndexRef.current === undefined) {
        hardDropFallbackTimerRef.current = window.setTimeout(() => setHardDrop(false), 900);
      }
    }

    if (shouldRotate && rect) {
      const action = rotationTapXRef.current < rect.left + rect.width / 2 ? "ccw" : "cw";
      navigator.vibrate?.(6);
      pulseRotation(action);
    }

  }, [pulseRotation, setHardDrop, setHorizontal, settings.hardDropEnabled, stopSlowDrop]);

  useEffect(() => {
    activeBlockIndexRef.current = activeBlockIndex;
    const target = hardDropTargetBlockIndexRef.current;
    if (hardDropRef.current && target !== undefined && activeBlockIndex !== undefined && activeBlockIndex !== target) {
      setHardDrop(false);
    }
  }, [activeBlockIndex, setHardDrop]);

  useEffect(() => {
    activeRef.current = active;
    updateGesture();
  }, [active, updateGesture]);

  useEffect(() => {
    if (!settings.hardDropEnabled) setHardDrop(false);
  }, [setHardDrop, settings.hardDropEnabled]);

  useEffect(() => {
    if (disabled || !settings.visible || settings.inputMode !== "gesture") {
      rotationTapCandidateRef.current = false;
      endGesture();
    }
  }, [disabled, endGesture, settings.inputMode, settings.visible]);

  useEffect(() => {
    const release = (event: PointerEvent) => endGesture(true, event.pointerId);
    const cancel = (event: PointerEvent) => {
      rotationTapCandidateRef.current = false;
      endGesture(false, event.pointerId);
    };
    const cancelWithoutPointer = () => {
      rotationTapCandidateRef.current = false;
      endGesture();
    };
    window.addEventListener("blur", cancelWithoutPointer);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", cancel);
    document.addEventListener("visibilitychange", cancelWithoutPointer);
    return () => {
      window.removeEventListener("blur", cancelWithoutPointer);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", cancel);
      document.removeEventListener("visibilitychange", cancelWithoutPointer);
      stopSlowDrop();
      setHardDrop(false);
    };
  }, [endGesture, setHardDrop, stopSlowDrop]);

  if (!settings.visible || settings.inputMode !== "gesture") return null;

  return (
    <div
      aria-label="盤面タッチ操作"
      className="board-touch-surface"
      onContextMenu={(event) => event.preventDefault()}
      onPointerCancel={(event) => {
        rotationTapCandidateRef.current = false;
        endGesture(false, event.pointerId);
      }}
      onPointerDown={(event) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        rotationTapCandidateRef.current = true;
        rotationTapXRef.current = event.clientX;
        setHardDrop(false);
        event.currentTarget.setPointerCapture(event.pointerId);
        gestureRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startPieceCenter: activeCenter(activeRef.current),
          currentX: event.clientX,
          targetY: event.clientY,
          startY: event.clientY,
          boardRect: rect,
          startedAt: performance.now(),
          lastMoveAt: performance.now(),
          lastMoveY: event.clientY,
          slowSwipe: false,
          verticalIntent: false,
          horizontalDragged: false,
        };
        setHorizontal(null);
        stopSlowDrop();
        updateGesture();
      }}
      onPointerMove={(event) => {
        if (gestureRef.current.pointerId !== event.pointerId) return;
        event.preventDefault();
        event.stopPropagation();
        const now = performance.now();
        const gesture = gestureRef.current;
        const elapsed = Math.max(1, now - gesture.startedAt);
        const moveElapsed = Math.max(1, now - gesture.lastMoveAt);
        const verticalDelta = event.clientY - gesture.startY;
        const boardRect = event.currentTarget.getBoundingClientRect();
        const cellWidth = boardRect.width / BOARD_COLUMNS;
        const cellHeight = boardRect.height / BOARD_ROWS;
        if (Math.abs(event.clientX - gesture.startX) > cellWidth * HORIZONTAL_DEAD_ZONE_CELLS) {
          gesture.horizontalDragged = true;
        }
        if (rotationTapCandidateRef.current && (
          Math.abs(event.clientX - rotationTapXRef.current) > cellWidth * HORIZONTAL_DEAD_ZONE_CELLS
          || Math.abs(verticalDelta) > cellHeight * HORIZONTAL_DEAD_ZONE_CELLS
        )) {
          rotationTapCandidateRef.current = false;
        }
        if (Math.abs(verticalDelta) > cellHeight * 0.5) gesture.verticalIntent = true;
        const downward = cellHeight > 0 && verticalDelta > cellHeight * DOWNWARD_GESTURE_THRESHOLD_CELLS;
        const instantVelocity = Math.max(0, (event.clientY - gesture.lastMoveY) / moveElapsed);
        const averageVelocity = Math.max(0, verticalDelta / elapsed);
        gesture.slowSwipe = downward && Math.max(instantVelocity, averageVelocity) <= SLOW_SWIPE_MAX_PX_PER_MS;
        gesture.lastMoveAt = now;
        gesture.lastMoveY = event.clientY;
        gestureRef.current.currentX = event.clientX;
        gestureRef.current.targetY = event.clientY;
        gestureRef.current.boardRect = boardRect;
        updateGesture();
      }}
      onPointerUp={(event) => {
        if (gestureRef.current.pointerId !== event.pointerId) return;
        event.preventDefault();
        event.stopPropagation();
        endGesture(true, event.pointerId);
      }}
    />
  );
}
